import * as React from 'react';
import type { SunEditorOptions } from 'suneditor/src/options';
import SunEditor from 'suneditor-react';
import * as sunEditorUtilModule from 'suneditor/src/lib/util';

import { formatAsElementId } from './Utils';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';
import SunEditorCore from 'suneditor/src/lib/core';

interface Props<T> extends React.HTMLProps<HtmlEditor<T>> {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: string;
	formId?: string;
	height?: number;
}

interface State {
	errorMsg?: string;
}

const sunEditorUtil = (sunEditorUtilModule as any).default;

type SunEditorUtilWithPatchFlag = typeof sunEditorUtil & {
	hasPatched?: boolean;
};

// HACK: SunEditor aparentemente va regenerando el HTML según vas escribiendo para si visualización. En nuestro
// caso, cuando tenemos una imagen y un texto y pulsamos [Enter] al generar el HTML y hacer las copias de los elementos
// y las cosillas que vaya haciendo debe tener un BUG, porque llega un element sin el campo style y hace fallar la aplicación.
// Para arreglarlo patcheamos la función en la que nos daba el error, para que si el elmento no lleva style falle (ya de paso hemos
// metido attributes). Además nos seguramos de no patchear varias veces "util", como webpack, etc por optimizacion pueden llegar
// a cachear imports estaríamos continuamente wrappeando utils 
const patchSunEditorCopyTagAttributes = () => {
	const util = sunEditorUtil as SunEditorUtilWithPatchFlag;
	if (util.hasPatched) {
		return;
	}

	const originalCopyTagAttributes = util.copyTagAttributes.bind(util);
	util.copyTagAttributes = (originEl: any, copyEl: any, blacklist: any) => {
		if (!copyEl?.style || !copyEl?.attributes) {
			return;
		}

		return originalCopyTagAttributes(originEl, copyEl, blacklist);
	};

	util.hasPatched = true;
};

patchSunEditorCopyTagAttributes();

const options: SunEditorOptions = {
	font: ['Arial', 'Comic Sans', 'Courier New', 'Georgia', 'Impact', 'Tahoma', 'Trebuchet', 'Verdana'],
	buttonList: [
		['undo', 'redo'],
		['font', 'fontSize', 'formatBlock'],
		// ['paragraphStyle', 'blockquote'],
		['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
		['fontColor', 'hiliteColor', 'textStyle'],
		['removeFormat'],
		['outdent', 'indent'],
		['align', 'horizontalRule', 'list', 'lineHeight'],
		['table', 'link', 'image'],
		// ['table', 'link', 'image', 'video', 'math'], // You must add the 'katex' library at options to use the 'math' plugin.
		// ['imageGallery'], // You must add the "imageGalleryUrl".
		['showBlocks', 'codeView']
		// ['save', 'template'],
		// '/', Line break
	]
};

export class HtmlEditor<T> extends React.Component<Props<T>, State> implements FormComponent {


	private ref = React.createRef<SunEditorCore>();

	constructor(props: Props<T>) {
		super(props);

		this.state = { errorMsg: '' };
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<any> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.ref.current?.getContents(true) || '');
	}

	render() {

		return (
			<div className={this.state.errorMsg ? 'has-error' : ''} id={formatAsElementId(this.getPath(), this.props.formId || '')}>
				<ErrorBlock msg={this.state.errorMsg} />
				<SunEditor getSunEditorInstance={suneditor => this.ref.current = suneditor}
					setOptions={options}
					height={`${this.props.height || 325}px`}
					lang='es'
					defaultValue={this.props.boundValue} />
			</div>
		);
	}
}

export type HtmlEditorCtor<T> = new (props: Props<T>) => HtmlEditor<T>;
